export type DateRangeChip = "24H" | "7D" | "30D";

const DAY_MS = 24 * 60 * 60 * 1000;
const ALL_CHIPS: DateRangeChip[] = ["24H", "7D", "30D"];

const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);

export function getDateRangeForChip(chip: DateRangeChip): { from: string; to: string } {
  const now = new Date();
  const to = toIsoDate(now);
  const days = chip === "24H" ? 1 : chip === "7D" ? 7 : 30;
  const from = toIsoDate(new Date(now.getTime() - days * DAY_MS));
  return { from, to };
}

export function isChipActive(
  chip: DateRangeChip,
  dateFrom: string | null,
  dateTo: string | null,
): boolean {
  if (!dateFrom || !dateTo) return false;
  const range = getDateRangeForChip(chip);
  return dateFrom === range.from && dateTo === range.to;
}

export function getActiveChip(
  dateFrom: string | null,
  dateTo: string | null,
): DateRangeChip | null {
  return ALL_CHIPS.find((c) => isChipActive(c, dateFrom, dateTo)) ?? null;
}

const CHIP_LABELS: Record<DateRangeChip, string> = {
  "24H": "Last 24 hours",
  "7D": "Last 7 days",
  "30D": "Last 30 days",
};

export function getChipLabel(chip: DateRangeChip): string {
  return CHIP_LABELS[chip];
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// SG-friendly long form (e.g. "25 May 2026") used in active-filter chips.
function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  const monthIdx = Number(m) - 1;
  if (!y || Number.isNaN(monthIdx) || monthIdx < 0 || monthIdx > 11) return iso;
  return `${Number(d)} ${MONTHS[monthIdx]} ${y}`;
}

export function formatDateRangeLabel(
  dateFrom: string | null,
  dateTo: string | null,
): string {
  const chip = getActiveChip(dateFrom, dateTo);
  if (chip) return getChipLabel(chip);
  if (dateFrom && dateTo) return `${formatIsoDate(dateFrom)} – ${formatIsoDate(dateTo)}`;
  if (dateFrom) return `From ${formatIsoDate(dateFrom)}`;
  if (dateTo) return `Until ${formatIsoDate(dateTo)}`;
  return "";
}
